import type {
  PlaceholderValue,
  PlaceholderValues,
} from "../portable-text/placeholder-types";

export type { PlaceholderValue, PlaceholderValues };

export type PlaceholderKey = string;

/**
 * A mapping of placeholder keys to their configuration/extraction rules.
 * Defines how to extract and transform values from source documents.
 */
export type PlaceholderMappings = Record<string, PlaceholderMapping>;

export interface PlaceholderOption {
  title: string;
  value: string;
  description?: string;
  example?: string;
  category?: string;
}

export interface PlaceholderMapping {
  path: string;
  transform?: (value: unknown) => PlaceholderValue;
  fallback?: PlaceholderValue;
  category?: string;
  description?: string;
}

/**
 * Some office documents store display-style labels in `locationName` (e.g. "151 - Northborough").
 * Placeholders should use the human location name only ("Northborough").
 */
export function stripLeadingOfficeIdFromLocationName(value: unknown): string {
  const raw =
    typeof value === "string" ? value : value != null ? String(value) : "";
  return raw.replace(/^\d+\s*-\s*/, "").trim();
}

export const OFFICE_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  address_line1: {
    category: "Location",
    description: "The street address of the office",
    path: "address.addressLine1",
  },
  country: {
    category: "Location",
    description: "The country of the office (US or CA)",
    path: "address.country",
  },
  current_year: {
    category: "Date/Time",
    description: "The current year",
    path: "_computed.current_year",
    transform: (): string => new Date().getFullYear().toString(),
  },
  founding_year: {
    category: "Business",
    description: "The year the franchise was founded",
    fallback: "",
    path: "franchiseOrganization.foundingYear",
    transform: (value: unknown): string => {
      if (typeof value === "string") {
        // Extract year from date string (format: YYYY-MM-DD or just YYYY)
        const year = value.substring(0, 4);
        return year;
      }
      return "";
    },
  },
  franchise_id: {
    category: "Business",
    description: "The franchise number associated with this office",
    path: "franchiseNumber",
    transform: (value: unknown): string => {
      if (typeof value === "number") {
        return value.toString();
      }
      return "";
    },
  },
  location_address: {
    category: "Location",
    description: "The address of the office",
    path: "address.name",
  },
  location_city: {
    category: "Location",
    description: "The city of the office location",
    path: "address.city",
  },
  location_name: {
    category: "Location",
    description:
      "The name of the office location (without a leading office ID prefix if present in CMS)",
    path: "locationName",
    transform: stripLeadingOfficeIdFromLocationName,
  },
  location_state: {
    category: "Location",
    description: "The state or province of the office location",
    path: "address.stateProvince",
  },
  location_zipcode: {
    category: "Location",
    description: "The zip / postal code of the office",
    path: "address.postalCode",
  },
  marketing_region_alias: {
    category: "Location",
    description:
      "A broader regional location label for marketing copy (e.g., 'Southwest Florida', 'Broward County', 'Sonoma County, CA')",
    path: "marketingRegionAlias",
  },
  office_id: {
    category: "Business",
    description: "The unique identifier for the office",
    path: "officeID",
    transform: (value: unknown): string => {
      if (typeof value === "number") {
        return value.toString();
      }
      return "";
    },
  },
  phone_number: {
    category: "Contact",
    description: "The main contact phone number",
    path: "phone",
  },
  seo_city_alias: {
    category: "Location",
    description:
      "A geographic/location qualifier for SEO differentiation (e.g., 'Chicago – Pilsen', 'Hillsborough County, FL')",
    path: "seoCityAlias",
  },
};

export const HOME_CARE_SERVICE_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  homeCareService_description: {
    category: "Service",
    description: "Short description of the service",
    path: "description",
  },
  homeCareService_serviceName: {
    category: "Service",
    description: "The name of the home care service",
    path: "serviceName",
  },
};

export const FRANCHISE_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  franchise_id: {
    category: "Business",
    description: "The franchise ID number",
    path: "id",
  },
  franchise_location_city: {
    category: "Location",
    description: "The city of the franchise",
    path: "location.city",
  },
  franchise_location_name: {
    category: "Location",
    description: "The franchise location name",
    path: "location.name",
  },
  franchise_location_postalCode: {
    category: "Location",
    description: "The postal code of the franchise",
    path: "location.postalCode",
  },
  franchise_location_state: {
    category: "Location",
    description: "The state/province of the franchise",
    path: "location.stateProvince",
  },
  franchise_phoneNumber: {
    category: "Contact",
    description: "The franchise phone number",
    path: "phoneNumber",
  },
  franchise_serviceArea: {
    category: "Business",
    description: "The service area of the franchise",
    path: "serviceArea",
  },
};

export const OFFICE_CONTENT_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  // Note: All officeContent fields with Portable Text are now automatically
  // introspected by document-schema-generator.ts. Only add entries here if
  // you need custom key names, transforms, or fallbacks.
};

export const NEIGHBORHOOD_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  neighborhood_body: {
    category: "Neighborhood",
    description: "Unique body copy for the neighborhood hub page",
    path: "body",
  },
  neighborhood_h1: {
    category: "Neighborhood",
    description: "Neighborhood H1 heading",
    path: "h1",
  },
};

export const ARTICLE_PLACEHOLDER_MAPPINGS: PlaceholderMappings = {
  article_audience: {
    category: "Article",
    description:
      "The audience label for the article (e.g. \u201cFor families\u201d)",
    path: "audience.label",
  },
  article_author_name: {
    category: "Article",
    description: "The article byline, blank when the article has no author",
    fallback: "",
    path: "authorName",
  },
  article_category: {
    category: "Article",
    description: "The primary (first) category label for the article",
    path: "categories.0.label",
  },
  article_meta_description: {
    category: "Article",
    description:
      "The article's search-result description \u2014 its Meta Description when set, otherwise its summary",
    path: "metaDescription",
  },
  article_published_year: {
    category: "Article",
    description: "The year the article was published",
    path: "publishedAt",
    transform: (value): string =>
      typeof value === "string" ? value.slice(0, 4) : "",
  },
  article_reading_time: {
    category: "Article",
    description: "Estimated reading time in whole minutes (e.g. \u201c5\u201d)",
    path: "readingTimeMinutes",
    transform: (value): string =>
      typeof value === "number" && Number.isFinite(value)
        ? String(Math.max(1, value))
        : "",
  },
  article_summary: {
    category: "Article",
    description: "The article summary shown on cards and in feeds",
    path: "summary",
  },
  article_title: {
    category: "Article",
    description: "The article title",
    path: "title",
  },
};

/**
 * Top-level fields omitted from placeholder introspection for a document type.
 * Neighborhood `name` is Studio-list only; public heading is `h1`. `route` is
 * a read-only display field, not page copy.
 *
 * Article `siteId` and `externalId` are plumbing: the first is a scoping
 * discriminator, the second an import key. Neither is page copy. `body` is
 * excluded because an article body is rendered by its own block, not injected
 * into another field as a placeholder.
 */
export const PLACEHOLDER_EXCLUDED_FIELDS: Record<string, readonly string[]> = {
  article: ["body", "externalId", "siteId"],
  neighborhood: ["name", "route"],
};

function isExcludedPlaceholderField(
  documentType: string,
  fieldPath: string,
): boolean {
  const excluded = PLACEHOLDER_EXCLUDED_FIELDS[documentType];
  if (!excluded) {
    return false;
  }

  const rootField = fieldPath.split(" → ")[0];
  return excluded.includes(rootField);
}

/**
 * Registry of placeholder mappings for document types
 *
 * NOTE: This registry is OPTIONAL. Document fields are automatically introspected
 * and made available as placeholders using their schema field names. Only add
 * entries here if you need to:
 * - Customize the placeholder key name (different from the field name)
 * - Add custom transformations (e.g., format dates, convert numbers)
 * - Provide fallback values
 * - Add custom categories or descriptions
 * - Access nested or computed values not directly in the schema
 *
 * @example
 * ```typescript
 * // Only needed for custom behavior:
 * const CARE_RESOURCE_PLACEHOLDER_MAPPINGS: Record<string, PlaceholderMapping> = {
 *   resource_title: { path: "title" }, // Rename "title" field to "resource_title" key
 *   founding_year: {
 *     path: "foundingDate",
 *     transform: (value) => value.substring(0, 4) // Extract year from date
 *   },
 * };
 *
 * DOCUMENT_PLACEHOLDER_REGISTRY.careResource = CARE_RESOURCE_PLACEHOLDER_MAPPINGS;
 * ```
 */
export const DOCUMENT_PLACEHOLDER_REGISTRY: Record<
  string,
  PlaceholderMappings
> = {
  article: ARTICLE_PLACEHOLDER_MAPPINGS,
  franchise: FRANCHISE_PLACEHOLDER_MAPPINGS,
  homeCareService: HOME_CARE_SERVICE_PLACEHOLDER_MAPPINGS,
  neighborhood: NEIGHBORHOOD_PLACEHOLDER_MAPPINGS,
  office: OFFICE_PLACEHOLDER_MAPPINGS,
  officeContent: OFFICE_CONTENT_PLACEHOLDER_MAPPINGS,
  // Add more document types here as needed
  // review: REVIEW_PLACEHOLDER_MAPPINGS,
};

export { isExcludedPlaceholderField };
